<script lang="ts">
export default {
  name: 'RadioGroup',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';

export interface RadioOption {
  label: string;
  value: string | number;
  disabled?: boolean;
}

const props = withDefaults(
  defineProps<{
    modelValue?: string | number;
    options: RadioOption[];
    disabled?: boolean;
    name?: string;
    layout?: 'horizontal' | 'vertical';
    class?: string;
  }>(),
  {
    disabled: false,
    layout: 'vertical',
    name: () => `radio-group-${Math.random().toString(36).slice(2, 11)}`,
  }
);

const emit = defineEmits<{
  'update:modelValue': [value: string | number];
}>();

const groupClasses = computed(() => {
  const classes = ['tw:form-control'];
  
  if (props.layout === 'horizontal') {
    classes.push('tw:flex tw:flex-row tw:gap-4');
  } else {
    classes.push('tw:flex tw:flex-col tw:gap-2');
  }
  
  if (props.class) {
    classes.push(props.class);
  }
  
  return classes.join(' ');
});

const handleChange = (value: string | number) => {
  if (!props.disabled) {
    emit('update:modelValue', value);
  }
};
</script>

<template>
  <div :class="groupClasses">
    <label
      v-for="option in options"
      :key="String(option.value)"
      class="tw:label tw:cursor-pointer tw:justify-start tw:gap-2"
      :class="{ 'tw:cursor-not-allowed tw:opacity-50': disabled || option.disabled }"
    >
      <input
        type="radio"
        :name="name"
        class="tw:radio"
        :class="{ 'tw:radio-disabled': disabled || option.disabled }"
        :value="option.value"
        :checked="modelValue === option.value"
        :disabled="disabled || option.disabled"
        @change="handleChange(option.value)"
      />
      <span class="tw:label-text tw:text-sm">{{ option.label }}</span>
    </label>
  </div>
</template>
